import { DataTestId, divideSimpleAmount, LocalizedCurrencySimpleValue, SimpleAmount } from '@mews-distributor/common';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedProperty, LocalizedPropertyObject, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews/text-keys';
import { Highlight, Stack, StackItem, Typography } from '@optimus-web/core';

import { ProductsBreakdown } from './ProductsBreakdown';

interface ReservationGroupBreakdownProps {
    resourceName?: LocalizedPropertyObject;
    rateName?: LocalizedPropertyObject;
    count: number;
    hourCount?: number;
    reservationId: string;
    unitPrice: SimpleAmount;
    totalPrice: SimpleAmount;
    showProductsBreakdown?: boolean;
}

export const ReservationGroupBreakdown = ({
    resourceName,
    rateName,
    hourCount,
    count,
    reservationId,
    unitPrice,
    totalPrice,
    showProductsBreakdown,
}: ReservationGroupBreakdownProps): JSX.Element => (
    <Stack vertical spacing="50" distribution="space-between">
        <StackItem>
            <Stack spacing="200" distribution="space-between">
                <Highlight size="small">
                    {resourceName ? (
                        <>
                            <LocalizedProperty property={resourceName} /> {` ${UnicodeEntity.PlusSign} `}
                        </>
                    ) : null}
                    {rateName ? <LocalizedProperty property={rateName} /> : null}
                </Highlight>

                <Highlight size="small">
                    <LocalizedCurrencySimpleValue value={totalPrice} />
                </Highlight>
            </Stack>
            {hourCount != null ? (
                <Stack data-test-id={DataTestId.AdditionalInfo} vertical>
                    <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                        <LocalizedText textKey={TextKey.HourPlural} />: {hourCount}
                    </Typography>
                    <Stack distribution="space-between">
                        <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                            {count > 1
                                ? `${count} ${UnicodeEntity.MultiplicationSign} ${hourCount} ${UnicodeEntity.MultiplicationSign} `
                                : `${hourCount} ${UnicodeEntity.MultiplicationSign} `}
                            <LocalizedCurrencySimpleValue value={divideSimpleAmount(unitPrice, hourCount)} />
                        </Typography>
                        <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                            <LocalizedCurrencySimpleValue value={totalPrice} />
                        </Typography>
                    </Stack>
                </Stack>
            ) : (
                <Typography
                    textStyle="bodySmallStrong"
                    variant="secondary"
                    data-test-id={DataTestId.AdditionalInfo}
                    as="div"
                >
                    {`${count} ${UnicodeEntity.MultiplicationSign} `}
                    <LocalizedCurrencySimpleValue value={unitPrice} />
                </Typography>
            )}
        </StackItem>
        {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            showProductsBreakdown ? <ProductsBreakdown reservationId={reservationId} /> : null
        }
    </Stack>
);
